import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import { AddButton } from '@/components/common/AddButton'
import { ListPageHeader } from '@/components/common/ListPageHeader'
import { useConfirm } from '@/components/common/ConfirmDialog'
import { useDeactivateTeam, useTeams } from './hooks'
import { TeamFormDialog } from './components/TeamFormDialog'
import { formatError } from './components/formatError'
import type { Team } from './types'

type TreeNode = { team: Team; children: TreeNode[] }

function buildTree(teams: Team[]): TreeNode[] {
  const byId = new Map<number, TreeNode>()
  teams.forEach(t => byId.set(t.id, { team: t, children: [] }))
  const roots: TreeNode[] = []
  teams.forEach(t => {
    const node = byId.get(t.id)!
    if (t.parentId == null) roots.push(node)
    else byId.get(t.parentId)?.children.push(node)
  })
  return roots
}

export function TeamsAdminPage() {
  const confirm = useConfirm()
  const teamsQ = useTeams(true)
  const del = useDeactivateTeam()
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<Team | null>(null)

  const tree = useMemo(() => teamsQ.data ? buildTree(teamsQ.data) : [], [teamsQ.data])

  return (
    <div className="flex flex-col w-full h-full">
      <ListPageHeader
        title="Zespoły"
        subtitle="Hierarchiczna struktura organizacji. Członkostwo w zespole-rodzicu obejmuje też pod-zespoły."
        actions={<AddButton onClick={() => setCreating(true)}>Dodaj zespół</AddButton>}
      />

      <div className="p-4">
        {teamsQ.isLoading && <p className="text-sm text-slate-500">Ładowanie zespołów…</p>}
        {teamsQ.data && tree.length === 0 && (
          <p className="text-sm text-slate-500">Brak zespołów. Utwórz pierwszy.</p>
        )}
        <div className="flex flex-col gap-1">
          {tree.map(node => (
            <TreeRow
              key={node.team.id}
              node={node}
              depth={0}
              onEdit={setEditing}
              onDelete={async t => {
                const ok = await confirm({
                  title: `Dezaktywować zespół "${t.name}"?`,
                  description: 'Zespół zostanie dezaktywowany.',
                  confirmLabel: 'Dezaktywuj',
                  variant: 'destructive',
                })
                if (ok) del.mutate(t.id, { onError: e => toast.error(formatError(e)) })
              }}
            />
          ))}
        </div>
      </div>

      {(creating || editing) && teamsQ.data && (
        <TeamFormDialog
          editing={editing}
          allTeams={teamsQ.data}
          onClose={() => { setCreating(false); setEditing(null) }}
        />
      )}
    </div>
  )
}

function TreeRow({ node, depth, onEdit, onDelete }: {
  node: TreeNode
  depth: number
  onEdit: (t: Team) => void
  onDelete: (t: Team) => void
}) {
  return (
    <>
      <div
        className="flex items-center gap-2 px-2 py-1 hover:bg-slate-50 rounded text-sm"
        style={{ paddingLeft: `${depth * 24 + 8}px` }}
      >
        {depth > 0 && <span className="text-slate-400">└</span>}
        <Link
          to={`/admin/teams/${node.team.id}`}
          className="text-sky-700 hover:underline flex-1"
        >{node.team.name}</Link>
        <span className="text-xs text-slate-400 font-mono">{node.team.code}</span>
        <button
          type="button"
          onClick={() => onEdit(node.team)}
          className="text-xs text-slate-600 hover:text-sky-700 px-1"
        >Edytuj</button>
        <button
          type="button"
          onClick={() => onDelete(node.team)}
          className="text-xs text-red-600 px-1"
        >Dezaktywuj</button>
      </div>
      {node.children.map(child => (
        <TreeRow
          key={child.team.id}
          node={child}
          depth={depth + 1}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      ))}
    </>
  )
}
